import { addDays, format } from "date-fns";
import { motion } from "framer-motion";
import { CalendarIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { WeekDay } from "@classview/shared-types";

import { AddSessionFormData } from "../types/add-session-schema";

export function RecurringSettingsElement() {
  const { sessionId } = useParams();
  const form = useFormContext<AddSessionFormData>();
  const isRecurring = form.watch("isRecurring");
  const recurringFrequency = form.watch("recurringFrequency");
  const endType = form.watch("endType");

  const isEditing = !!sessionId;

  return (
    <>
      <FormField
        control={form.control}
        name="isRecurring"
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-row items-center">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isEditing}
                />
              </FormControl>
              <div className="ml-2 mt-0">
                <FormLabel className="text-base">Session Repeats</FormLabel>
              </div>
            </div>
          </FormItem>
        )}
      />
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={
          isRecurring
            ? { opacity: 1, height: "auto" }
            : { opacity: 0, height: 0 }
        }
        transition={{ duration: 0.3 }}
        style={{ overflowClipMargin: 5 }}
        className="overflow-clip"
      >
        <FormField
          control={form.control}
          name="recurringFrequency"
          render={({ field }) => (
            <FormItem className="space-y-2 pt-4">
              <FormLabel>Frequency</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-col space-y-1"
                >
                  <FormItem className="flex space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="DAILY" />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-normal">Daily</FormLabel>
                      <FormDescription>Session repeats daily</FormDescription>
                    </div>
                  </FormItem>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={
                      recurringFrequency === "DAILY"
                        ? { opacity: 1, height: "auto" }
                        : { opacity: 0, height: 0 }
                    }
                    transition={{ duration: 0.3 }}
                    style={{ overflowClipMargin: 5 }}
                    className="overflow-clip"
                  >
                    <FormField
                      control={form.control}
                      name="isRepeatOnWeekdaysOnly"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Repeat on weekdays only</FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />
                  </motion.div>
                  <FormItem className="flex space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="WEEKLY" />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-normal">Weekly</FormLabel>
                      <FormDescription>Session repeats weekly</FormDescription>
                    </div>
                  </FormItem>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={
                      recurringFrequency === "WEEKLY"
                        ? { opacity: 1, height: "auto" }
                        : { opacity: 0, height: 0 }
                    }
                    transition={{ duration: 0.3 }}
                    style={{ overflowClipMargin: 5 }}
                    className="overflow-clip"
                  >
                    <div className="space-y-3 rounded-md border border-border p-4">
                      <FormField
                        control={form.control}
                        name="weeklyInterval"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-x-2 space-y-0">
                            <FormLabel>Recure every</FormLabel>
                            <div className="flex items-center gap-x-2">
                              <FormControl className="w-16">
                                <Input
                                  {...field}
                                  type="number"
                                  min={1}
                                  max={52}
                                />
                              </FormControl>
                              <span className="text-sm text-muted-foreground">
                                {field.value === 1 ? "week" : "weeks"}
                              </span>
                            </div>

                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="weeklyDays"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormDescription>
                              Choose which days the session should repeat
                            </FormDescription>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { value: WeekDay.MONDAY, label: "Mon" },
                                { value: WeekDay.TUESDAY, label: "Tue" },
                                { value: WeekDay.WEDNESDAY, label: "Wed" },
                                { value: WeekDay.THURSDAY, label: "Thu" },
                                { value: WeekDay.FRIDAY, label: "Fri" },
                                { value: WeekDay.SATURDAY, label: "Sat" },
                                { value: WeekDay.SUNDAY, label: "Sun" },
                              ].map((day) => (
                                <Button
                                  key={day.value}
                                  type="button"
                                  variant={
                                    field.value?.includes(day.value)
                                      ? "default"
                                      : "outline"
                                  }
                                  size="default"
                                  className="h-8 px-3"
                                  onClick={() => {
                                    const currentDays = field.value || [];
                                    const newDays = currentDays.includes(
                                      day.value,
                                    )
                                      ? currentDays.filter(
                                          (d) => d !== day.value,
                                        )
                                      : [...currentDays, day.value];
                                    field.onChange(newDays);
                                  }}
                                >
                                  {day.label}
                                </Button>
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </motion.div>

                  <FormItem className="flex space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="MONTHLY" />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-normal">Monthly</FormLabel>
                      <FormDescription>
                        Session happens on this date every month
                      </FormDescription>
                    </div>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Ends</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-col space-y-1"
                >
                  <FormItem className="flex space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="DATE" />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-normal">
                        On a particular date
                      </FormLabel>
                      <FormDescription>
                        Sessions wont repeat after this date
                      </FormDescription>
                    </div>
                  </FormItem>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={
                      endType === "DATE"
                        ? { opacity: 1, height: "auto" }
                        : { opacity: 0, height: 0 }
                    }
                    transition={{ duration: 0.3 }}
                    style={{ overflowClipMargin: 5 }}
                    className="overflow-clip"
                  >
                    <FormField
                      control={form.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  className={cn(
                                    "w-[240px] pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground",
                                  )}
                                  disabled={form.formState.isSubmitting}
                                >
                                  {field.value ? (
                                    format(field.value, "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) =>
                                  date < addDays(new Date(), -1)
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </motion.div>
                  <FormItem className="flex space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="NB_REPETITIONS" />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="font-normal">
                        After a number of repetitions
                      </FormLabel>
                      <FormDescription>
                        Sessions wont repeat after the number of repetitions has
                        been reached
                      </FormDescription>
                    </div>
                  </FormItem>

                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={
                      endType === "NB_REPETITIONS"
                        ? { opacity: 1, height: "auto" }
                        : { opacity: 0, height: 0 }
                    }
                    transition={{ duration: 0.3 }}
                    style={{ overflowClipMargin: 5 }}
                    className="overflow-clip"
                  >
                    <div className="rounded-md border border-border px-4 py-3">
                      <FormField
                        control={form.control}
                        name="nbRepetitions"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel isRequired>
                              Number of repetitions
                            </FormLabel>
                            <FormControl className="w-16">
                              <Input {...field} type="number" min={1} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </motion.div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </motion.div>
    </>
  );
}
